import {
  withStyles,
  CircleButton,
  Box,
  Card,
  GetColorStatus,
  parseJWT,
  TextField,
  PARTNER_ACCOUNT_STATUSES,
} from 'vopay';
import type {
  AccountParentAccountInfo,
  ApiCredentials,
  EncodedCredentials,
  PartnerAccountStatus,
  SupportedCurrencies,
} from 'vopay';

import { Fragment, FunctionComponent, useContext, useEffect, useState } from 'react';
import { Prompt, useParams, useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import SubAccountTabs from './SubAccountTabs';
import AccountPermissionsDrawer from '../Account/AccountPermissionsDrawer/AccountPermissionsDrawer';
import AccountContext from '../AccountContext';

import { usePendingData } from '@/providers/PendingData.provider';
import { useModuleFeatureFlag } from '@/providers/FeatureFlag.provider';
import { usePartnerData } from '@/providers/PartnerData.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useDialog, type OpenDialogProps } from '@/providers/Dialog.provider';
import { Title, UtilButton, TransactionStatus } from '@/components/UI';
import CopyButton from '@/components/UI/CopyButton/CopyButton';
import SendEmailDialog, { type SendEmailDialogResult } from '../Account/SendEmailDIalog';

import ActiveIcon from '../Account/svg/Active.svg';
import PendingIcon from '../Account/svg/Pending.svg';

import useFetchPartnerAccounts from '@/hooks/useApi/useFetchPartnerAccounts';
import usePartnerBearer from '@/hooks/useApi/usePartnerBearer';
import { usePartnerAccountStatusLabel } from '@/hooks';

import styles from './SubAccount.module.scss';
import type { AccountsHandlers, Balances as Balance } from '../types';

const cn = withStyles(styles);

// `accountStatus` reaches this page as a URL query param (written from a real
// `AccountBalance.AccountStatus`, so in practice always a valid response status), but `URLSearchParams.get`
// loses the type back to `string`. Narrow it against the canonical list before handing it to the
// union-typed resolver; an unrecognized (e.g. hand-edited) value renders raw instead.
const isPartnerAccountStatus = (value: string): value is PartnerAccountStatus =>
  PARTNER_ACCOUNT_STATUSES.some((status) => status === value);

type SubAccountProps = {
  hasValidOnboardingUrl: boolean | null;
  handlers: AccountsHandlers;
  onBoardingLink: string | null;
};

type Chain = {
  Label: string;
  Value: string;
};

type BreadcrumbsProps = {
  parentAccounts: AccountParentAccountInfo[];
  onClick: (id: string, index: number) => void;
};

const AccountBreadcrumbs: FunctionComponent<BreadcrumbsProps> = ({ parentAccounts, onClick }) => {
  const [expanded, setExpanded] = useState(false);
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const buildTree = (): Chain[] => {
    if (parentAccounts.length === 0) return [];
    const root = parentAccounts.find((acc) => acc.ParentAccountID === null);
    if (!root) return [];
    const chain: Chain[] = [{ Label: root.AccountName, Value: root.AccountID }];
    let current = parentAccounts.find((acc) => acc.ParentAccountID === root.AccountID);

    while (current) {
      chain.push({ Label: current.AccountName, Value: current.AccountID });
      current = parentAccounts.find((acc) => acc.ParentAccountID === current!.AccountID);
    }

    return chain;
  };

  const tree = buildTree();
  if (tree.length === 0) return null;

  const renderItem = (item: Chain, index: number) => (
    <Fragment key={item.Value}>
      <button type="button" onClick={() => onClick(item.Value, index)}>
        {item.Label}
      </button>

      {index < tree.length - 1 && ' > '}
    </Fragment>
  );

  return (
    <nav aria-label={tGlobal('breadcrumbs')}>
      <span className={cn('breadcrumbs')}>
        {tree.length <= 2 || expanded ? (
          tree.map(renderItem)
        ) : (
          <>
            {renderItem(tree[0], 0)}

            <button type="button" onClick={() => setExpanded(true)}>
              ...
            </button>

            {' > '}
            {renderItem(tree[tree.length - 1], tree.length - 1)}
          </>
        )}
      </span>
    </nav>
  );
};

type FC = FunctionComponent<SubAccountProps>;

const SubAccount: FC = ({ hasValidOnboardingUrl, onBoardingLink, handlers }: SubAccountProps) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.SubAccount' });
  const { resolveStatusLabel } = usePartnerAccountStatusLabel();
  const { details, transactions, balances } = useContext(AccountContext);
  const { hasPendingData } = usePendingData();
  const history = useHistory();

  const { canAccessOnboardingTokenPanel, canSeeAccountCredential, canUpdatePermissions, canSendOnboardingEmail } =
    useModuleFeatureFlag('accounts');

  const [isPermissionsOpen, setIsPermissionsOpen] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<{ id: string; index: number } | null>(null);
  const [fetchPartnerAccountsResponse, refreshPartnerAccounts] = useFetchPartnerAccounts({});

  const { accountId } = useParams<{ accountId: string }>();
  const partnerBearer = usePartnerBearer(details[accountId]?.PartnerAccountID ?? '');

  const alertDialog = useAlertDialog();
  const dialog = useDialog();

  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  const query = new URLSearchParams(useLocation().search);

  const balance: Balance = {
    account: query.get('account') || '0',
    pending: query.get('pending') || '0',
    available: query.get('available') || '0',
    immediate: query.get('immediate') || '0',
    reserve: query.get('reserve') || '0',
    currency: (query.get('currency') as SupportedCurrencies) || 'CAD',
    isActive: query.get('isActive') === 'true',
    accountStatus: query.get('accountStatus') || '',
    security: query.get('security') || '',
  };

  useEffect(() => {
    if (!fetchPartnerAccountsResponse || !pendingNavigation) return;

    const { id, index } = pendingNavigation;

    const matched =
      fetchPartnerAccountsResponse.balances.find((b) => b.AccountID === id && b.Currency === balance.currency) ??
      fetchPartnerAccountsResponse.balances.find((b) => b.AccountID === id);

    if (!matched) return;

    setPendingNavigation(null);

    const search = new URLSearchParams({
      accountId: matched.AccountID,
      isActive: String(matched.IsActive),
      accountStatus: matched.AccountStatus,
      currency: matched.Currency,
      pending: matched.PendingBalance,
      available: matched.AvailableBalance,
      account: matched.AccountBalance,
      reserve: matched.Reserve,
      immediate: matched.AvailableImmediately,
    });

    if (index === 0) {
      history.push({ pathname: `/accounts/${id}`, search: search.toString() });
    } else {
      history.push({ pathname: `/accounts/sub-account/${id}`, search: search.toString() });
    }
  }, [fetchPartnerAccountsResponse]);

  const handleCopyClick = () => {
    if (onBoardingLink) void navigator?.clipboard?.writeText(onBoardingLink);
  };

  if (!details[accountId]) return null;

  const account = balances.find((acc) => acc.AccountID === accountId);

  const isPending = canAccessOnboardingTokenPanel && balance.accountStatus === 'Application Pending';
  const credentials = canSeeAccountCredential && balance.isActive ? getCredentials(details[accountId].token) : null;

  const handleSendEmail = async () => {
    const BoundDialog = ({ onConfirm, onCancel }: OpenDialogProps<SendEmailDialogResult>) => (
      <SendEmailDialog
        onConfirm={onConfirm}
        onCancel={onCancel}
        email={details[accountId].contact.email}
        name={details[accountId].accountName}
        link={onBoardingLink || ''}
        accountId={accountId}
        bearer={partnerBearer}
      />
    );

    const result = await dialog.open(BoundDialog);
    if (!result) return;

    if (result.status === 'error') {
      alertDialog.error(t('OnboardingEmail.errorTitle'), result.errorMessage);
    }
  };

  /**
   * `parseJWT` throws on anything it can't split into JWT segments and base64-decode, so a corrupt or
   * truncated account token must not be dereferenced blind — it used to take the whole sub-account page
   * down with it. A token that won't decode means no credentials to show, not a blank page.
   */
  function getCredentials(token: string): EncodedCredentials | ApiCredentials | null {
    try {
      const json = parseJWT(token);
      const defaults: EncodedCredentials = { AccountID: '', ApiKey: '', ApiSharedSecret: '' };

      return isValidCredentials(json) ? json : defaults;
    } catch (error) {
      console.error(error);

      return null;
    }
  }

  function isValidCredentials(json: unknown): json is EncodedCredentials | ApiCredentials {
    return (
      Object.prototype.hasOwnProperty.call(json, 'AccountID') && Object.prototype.hasOwnProperty.call(json, 'ApiKey')
    );
  }

  function isEncodedCredentials(creds: EncodedCredentials | ApiCredentials): creds is EncodedCredentials {
    return Object.prototype.hasOwnProperty.call(creds, 'ApiSharedSecret');
  }

  function getSecretLabel(creds: EncodedCredentials | ApiCredentials): string {
    return isEncodedCredentials(creds) ? t('Credentials.apiSharedSecret') : t('Credentials.apiSignature');
  }

  function getSecretValue(creds: EncodedCredentials | ApiCredentials): string {
    return isEncodedCredentials(creds) ? creds.ApiSharedSecret : (creds as ApiCredentials).ApiSignature;
  }

  return (
    <Box gapSize="24px" flx="col" maxwidth="1120px" className={cn('accountContainer')}>
      <UtilButton
        size="medium"
        iconLeft="chevron_left"
        onClick={() => {
          history.goBack();
        }}
      >
        {t('backButton')}
      </UtilButton>

      {Object.values(details).length && (
        <>
          <Card style={{ padding: '0px' }}>
            <div className={cn('header', credentials && 'headerWithCredentials')}>
              <div className={cn('headerRight')}>
                <img
                  src={balance.isActive ? ActiveIcon : PendingIcon}
                  alt={t(balance.isActive ? 'iconAltActive' : 'iconAltPending', {
                    defaultValue: balance.isActive ? 'Active Account' : 'Pending Account',
                  })}
                  className={cn('accountIcon')}
                />

                <div className={cn('titleGroupContainer')}>
                  {account?.ParentAccounts && (
                    <AccountBreadcrumbs
                      parentAccounts={account.ParentAccounts}
                      onClick={(id, index) => {
                        setPendingNavigation({ id, index });
                        refreshPartnerAccounts();
                      }}
                    />
                  )}

                  <div className={cn('titleGroup')}>
                    <Title level="3">{details[accountId].accountName}</Title>
                    <span className={cn('currencySepearator')}>|</span>{' '}
                    <span className={cn('currency')}>{balance.currency}</span>
                  </div>

                  {canUpdatePermissions && (
                    <UtilButton
                      className={cn('viewPermissionsButton')}
                      size="medium"
                      onClick={() => setIsPermissionsOpen(true)}
                      iconRightProps={{
                        fillAmount: 1,
                        icon: 'edit',
                      }}
                    >
                      {t('viewPermissions')}
                    </UtilButton>
                  )}
                </div>
              </div>

              <div className={cn('headerLeft')}>
                <div className={cn('statusContainer')}>
                  {balance.isActive ? (
                    <TransactionStatus label={resolveStatusLabel('Active')} status="positive" />
                  ) : (
                    !!balance.accountStatus && (
                      <TransactionStatus
                        label={
                          isPartnerAccountStatus(balance.accountStatus)
                            ? resolveStatusLabel(balance.accountStatus)
                            : balance.accountStatus
                        }
                        status={GetColorStatus(balance.accountStatus)}
                      />
                    )
                  )}
                </div>

                {isPending && (
                  <div className={cn('onboardingUrl')}>
                    <TextField name="onboardingUrl" readOnly variant="outlineShadow" value={onBoardingLink || ''} />

                    <div className={cn('onboardingActions')}>
                      <CircleButton
                        colorScheme="action"
                        icon="content_copy"
                        onClick={handleCopyClick}
                        disabled={!hasValidOnboardingUrl || isRoleViewOnly}
                      />

                      {canSendOnboardingEmail && (
                        <CircleButton
                          colorScheme="action"
                          icon="send"
                          onClick={handleSendEmail}
                          disabled={!hasValidOnboardingUrl || isRoleViewOnly}
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {credentials && (
              <div className={cn('credentials')}>
                <div className={cn('credentialItem')}>
                  <span className={cn('credentialLabel')}>{t('Credentials.accountId')}</span>

                  <div className={cn('credentialValue')}>
                    <span>{credentials.AccountID}</span>
                    <CopyButton content={credentials.AccountID} />
                  </div>
                </div>

                <div className={cn('credentialItem')}>
                  <span className={cn('credentialLabel')}>{t('Credentials.apiKey')}</span>

                  <div className={cn('credentialValue')}>
                    <span>{credentials.ApiKey}</span>
                    <CopyButton content={credentials.ApiKey} />
                  </div>
                </div>

                <div className={cn('credentialItem')}>
                  <span className={cn('credentialLabel')}>{getSecretLabel(credentials)}</span>

                  <div className={cn('credentialValue')}>
                    <span>{getSecretValue(credentials)}</span>
                    <CopyButton content={getSecretValue(credentials)} />
                  </div>
                </div>
              </div>
            )}
          </Card>

          <AccountPermissionsDrawer
            isOpen={isPermissionsOpen}
            onClose={() => setIsPermissionsOpen(false)}
            accountName={details[accountId].accountName}
          />

          <SubAccountTabs transactions={transactions} handlers={handlers} />
          <Prompt when={hasPendingData} message={t('unsavedChangesPrompt')} />
        </>
      )}
    </Box>
  );
};

export default SubAccount;
